<template>
  <Teleport to="body" :disabled="isDisabledTeleport">
    <Transition name="modal">
      <div
        v-if="isVisible"
        class="modal-container"
        :class="styles"
        @pointerdown.self="closeModalContainer"
        v-bind="$attrs"
      >
        <div class="modal">
          <div v-if="isLoading" class="modal-spinner">
            <SpinnerRadial />
          </div>
          <template v-else>
            <div class="modal-header">
              <slot name="header"></slot>
            </div>
            <div ref="modalBody" class="modal-body">
              <slot></slot>
            </div>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts">
import { PropType } from "vue";
import SpinnerRadial from "~/components/loaders/SpinnerRadial.vue";

export default defineNuxtComponent({
  components: { SpinnerRadial },
  props: {
    modalBody: {
      type: Object as PropType<HTMLElement | null>,
      required: false
    },
    isShowModal: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    isLoading: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    isDisabledTeleport: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    styles: {
      type: Object,
      default: () => {},
    },
    nestedDialog: {
      type: Boolean,
      default: false,
    },
    preventClose: {
      type: Boolean,
      default: false,
    }
  },
  emits: ["close", "update:modalBody"],
  inheritAttrs: false,
  setup(props, { emit }) {
    const route = useRoute();

    const isVisible = ref(false);

    const modalBody = ref<HTMLElement | null>(null);
    watch(() => modalBody.value, (newValue) => {
      if (newValue) emit("update:modalBody", newValue);
      else emit("update:modalBody", null);
    });

    const preventScroll = (event: Event) => {
      const allModalBodiesArray = Array.from(document.getElementsByClassName("modal-body"));

      if (allModalBodiesArray.length && !allModalBodiesArray.some((modalBody) =>
        modalBody.contains(event.target as Node))) {
        event.preventDefault();
      }
    };

    const toggleScrollbar = (value: boolean): void => {
      if (props.nestedDialog) {
        return;
      }

      if (value) {
        document.documentElement.style.setProperty(
          "--scrollbar-compensate",
          `${getScrollbarWidth()}px`
        );
        document.documentElement.classList.add("no-scroll");
        document.addEventListener("touchmove", preventScroll, { passive: false });
      } else {
        document.documentElement.style.removeProperty("--scrollbar-compensate");
        document.documentElement.classList.remove("no-scroll");
        document.removeEventListener("touchmove", preventScroll);
      }
    };

    watch(
      () => props.isShowModal,
      (newVal) => {
        isVisible.value = newVal;
        toggleScrollbar(newVal);
      }
    );

    const closeModalContainer = () => {
      if(props.preventClose) return;
      // These ref and with watcher above are neccessary for correct transition working in LazyLoading component scenario
      isVisible.value = false;
      emit("close");
    };

    onMounted(() => {
      toggleScrollbar(true);
      isVisible.value = props.isShowModal;
    });

    onUnmounted(() => {
      toggleScrollbar(false);
    });

    return {
      closeModalContainer,
      isVisible,
      modalBody,
    };
  },
});
</script>
